/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback } from 'react'
import ConfirmDialog from '../components/ui/confirm-dialog'

interface ConfirmOptions {
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'destructive'
  suppressKey?: string // if provided, user can opt out of seeing again
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined)

// Moved hook to separate file for fast refresh rule compliance
export { ConfirmContext }

interface ConfirmState {
  open: boolean
  options: ConfirmOptions
  resolve: (value: boolean) => void
  dontShowAgainSelected: boolean
}

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<ConfirmState | null>(null)

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    // If suppressed in localStorage, resolve immediately
    if (options.suppressKey) {
      try {
        if (typeof window !== 'undefined') {
          const stored = window.localStorage.getItem(`confirm:suppress:${options.suppressKey}`)
          if (stored === '1') return Promise.resolve(true)
        }
      } catch {
        // Ignore storage access errors
      }
    }
    return new Promise((resolve) => {
      setState({
        open: true,
        options,
        resolve,
        dontShowAgainSelected: false,
      })
    })
  }, [])

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open && state) {
      state.resolve(false)
      setState(null)
    }
  }, [state])

  const handleConfirm = useCallback(() => {
    if (state) {
      // Persist suppression preference if chosen
      if (state.options.suppressKey && state.dontShowAgainSelected) {
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(`confirm:suppress:${state.options.suppressKey}`, '1')
          }
        } catch {
          // Ignore storage access errors
        }
      }
      state.resolve(true)
      setState(null)
    }
  }, [state])

  const contextValue: ConfirmContextValue = {
    confirm,
  }

  return (
    <ConfirmContext.Provider value={contextValue}>
      {children}
      {state && (
        <ConfirmDialog
          open={state.open}
          onOpenChange={handleOpenChange}
          title={state.options.title}
          description={state.options.description}
          confirmText={state.options.confirmText}
          cancelText={state.options.cancelText}
          variant={state.options.variant}
          onConfirm={handleConfirm}
          suppressKey={state.options.suppressKey}
          dontShowAgainSelected={state.dontShowAgainSelected}
          onToggleDontShowAgain={(v: boolean) => setState(s => s ? { ...s, dontShowAgainSelected: v } : s)}
        />
      )}
    </ConfirmContext.Provider>
  )
}

// Separate file export for the hook, but keep export here for backwards compatibility (will be removed once refactored fully)
export const useConfirm = (): ConfirmContextValue => {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider')
  }
  return context
}
